import { useState } from 'react';

import { PropTypes } from 'prop-types';

import { FormattedMessage, FormattedNumber, useIntl } from '@zenbusiness/application-commons-intl';
import AccordionDetails from '@zenbusiness/zui/AccordionDetails';
import AccordionSummary from '@zenbusiness/zui/AccordionSummary';
import Box from '@zenbusiness/zui/Box';
import Chip from '@zenbusiness/zui/Chip';
import Stack from '@zenbusiness/zui/Stack';

import {
  InvoiceCardRoot,
  InvoiceCardAccordion,
  InvoiceCardHeader,
  InvoiceCardTitleRow,
  InvoiceCardAvatar,
  InvoiceCardAmountColumn,
  InvoiceCardTitle,
  InvoiceCardDueDate,
  InvoiceCardAmount,
  InvoiceCardDetailsSurface,
  InvoiceCardDetailsContent,
  InvoiceCardDivider,
  InvoiceCardLockIcon,
  InvoiceCardLockedMessage,
  InvoiceCardActions,
  InvoiceCardPrimaryButton,
  InvoiceCardPaymentLogo,
  InvoiceCardSecondaryButton
} from './InvoiceCard.style';
import { PAYMENT_LOGO_URL } from '../../constants';

const MESSAGE_IDS = {
  title: 'dashboard.invoicePayments.invoiceCard.title',
  dueDate: 'dashboard.invoicePayments.invoiceCard.dueDate',
  details: 'dashboard.invoicePayments.invoiceCard.details',
  unpaid: 'dashboard.invoicePayments.invoiceCard.unpaid',
  pay: 'dashboard.invoicePayments.invoiceCard.pay',
  payWithAnotherMethod: 'dashboard.invoicePayments.invoiceCard.payWithAnotherMethod',
  payPreviousFirst: 'dashboard.invoicePayments.invoiceCard.payPreviousFirst',
  payLogoAlt: 'dashboard.invoicePayments.invoiceCard.payLogoAlt'
};

function InvoiceCard({
  amount,
  avatarUrl,
  dueDate,
  isLocked = false,
  onPay,
  onPayWithAnotherMethod
}) {
  const { formatMessage: fm } = useIntl();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  return (
    <InvoiceCardRoot isLocked={isLocked}>
      <InvoiceCardHeader>
        <InvoiceCardTitleRow>
          <Box>
            <InvoiceCardTitle>
              <FormattedMessage id={MESSAGE_IDS.title} />
            </InvoiceCardTitle>
            <InvoiceCardDueDate>
              <FormattedMessage id={MESSAGE_IDS.dueDate} values={{ dueDate }} />
            </InvoiceCardDueDate>
          </Box>
          {!isLocked && avatarUrl && <InvoiceCardAvatar src={avatarUrl} />}
        </InvoiceCardTitleRow>
        <InvoiceCardAmountColumn>
          <InvoiceCardAmount>
            {/* eslint-disable-next-line react/style-prop-object */}
            <FormattedNumber currency="USD" style="currency" value={amount} />
          </InvoiceCardAmount>
          <Chip color="error" label={<FormattedMessage id={MESSAGE_IDS.unpaid} />} size="sm" />
        </InvoiceCardAmountColumn>
      </InvoiceCardHeader>

      <InvoiceCardAccordion
        clickstreamProps={{ elementName: 'invoice_details_accordion' }}
        disableGutters
        expanded={isLocked ? false : detailsExpanded}
        isLocked={isLocked}
        onChange={(event, expanded) => {
          if (!isLocked) {
            setDetailsExpanded(expanded);
          }
        }}
      >
        <AccordionSummary aria-disabled={isLocked}>
          <FormattedMessage id={MESSAGE_IDS.details} />
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceCardDetailsSurface>
            <InvoiceCardDetailsContent>
              {/* Placeholder for invoice detail content — wired in a future ticket */}
            </InvoiceCardDetailsContent>
          </InvoiceCardDetailsSurface>
        </AccordionDetails>
      </InvoiceCardAccordion>

      {isLocked ? (
        <>
          <InvoiceCardDivider />
          <InvoiceCardLockedMessage>
            <InvoiceCardLockIcon aria-hidden />
            <FormattedMessage id={MESSAGE_IDS.payPreviousFirst} />
          </InvoiceCardLockedMessage>
        </>
      ) : (
        <InvoiceCardActions>
          <InvoiceCardPrimaryButton
            clickstreamProps={{ elementName: 'pay_invoice' }}
            onClick={onPay}
          >
            <Stack alignItems="center" direction="row" justifyContent="center" spacing={1}>
              <FormattedMessage id={MESSAGE_IDS.pay} />
              <InvoiceCardPaymentLogo
                alt={fm({ id: MESSAGE_IDS.payLogoAlt })}
                src={PAYMENT_LOGO_URL}
              />
            </Stack>
          </InvoiceCardPrimaryButton>
          <InvoiceCardSecondaryButton
            clickstreamProps={{ elementName: 'pay_other_method' }}
            onClick={onPayWithAnotherMethod}
          >
            <FormattedMessage id={MESSAGE_IDS.payWithAnotherMethod} />
          </InvoiceCardSecondaryButton>
        </InvoiceCardActions>
      )}
    </InvoiceCardRoot>
  );
}

InvoiceCard.propTypes = {
  amount: PropTypes.number.isRequired,
  avatarUrl: PropTypes.string,
  dueDate: PropTypes.string.isRequired,
  isLocked: PropTypes.bool,
  onPay: PropTypes.func,
  onPayWithAnotherMethod: PropTypes.func
};

export default InvoiceCard;
